import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line, CartesianGrid } from "recharts";
import { Loader2, Trophy } from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";

const COLORS = ["#E85D04", "#0D1B2A", "#059669", "#0369A1", "#7C3AED", "#DB2777", "#EA580C", "#0D9488", "#B91C1C", "#4338CA"];

const AdminAnalytics = () => {
  const { api } = useAdminAuth();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setData(r.data)); }, [api]);

  if (!data) return <div className="p-10 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#0D1B2A]/60" /></div>;

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="admin-analytics-page">
      <div>
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E85D04]">Analytics</span>
        <h1 className="mt-1 font-display text-3xl md:text-4xl font-semibold tracking-tight">Platform analytics</h1>
        <p className="mt-1 text-sm text-[#0D1B2A]/60">Last 12 weeks activity, category mix, and top campaigns.</p>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-2xl bg-white border border-[#0D1B2A]/10 p-6" data-testid="chart-campaigns">
          <h2 className="font-display text-lg font-semibold">Campaigns created per week</h2>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.weeks}>
                <CartesianGrid strokeDasharray="3 3" stroke="#0D1B2A11" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#0D1B2A" }} />
                <YAxis tick={{ fontSize: 11, fill: "#0D1B2A" }} />
                <Tooltip />
                <Bar dataKey="campaigns" fill="#E85D04" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-[#0D1B2A]/10 p-6" data-testid="chart-gmv">
          <h2 className="font-display text-lg font-semibold">GMV per week</h2>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.weeks}>
                <CartesianGrid strokeDasharray="3 3" stroke="#0D1B2A11" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#0D1B2A" }} />
                <YAxis tick={{ fontSize: 11, fill: "#0D1B2A" }} />
                <Tooltip />
                <Line type="monotone" dataKey="gmv" stroke="#0D1B2A" strokeWidth={2} dot={{ fill: "#E85D04", r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-[#0D1B2A]/10 p-6" data-testid="chart-category">
          <h2 className="font-display text-lg font-semibold">Campaigns by category</h2>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.by_category} dataKey="count" nameKey="category" outerRadius={90} label={{ fontSize: 11 }}>
                  {data.by_category.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-[#0D1B2A]/10 p-6" data-testid="top-campaigns">
          <h2 className="font-display text-lg font-semibold flex items-center gap-2"><Trophy className="h-5 w-5 text-[#E85D04]" /> Top 5 by raised</h2>
          <ol className="mt-4 space-y-3">
            {data.top_campaigns.map((c, i) => (
              <li key={c.id} className="flex items-center gap-3 p-3 rounded-xl bg-[#FFFBF5] border border-[#0D1B2A]/5">
                <div className="grid place-items-center h-8 w-8 rounded-lg bg-[#0D1B2A] text-[#E85D04] font-display font-bold">{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{c.title}</div>
                  <div className="text-xs text-[#0D1B2A]/60 truncate">{c.creator_name} · {c.category}</div>
                </div>
                <div className="font-display font-semibold">${(c.raised / 1000).toFixed(0)}K</div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
